import { Alert, Button, Form, Input, Modal } from "antd";
import { message } from "utils/ciq-message";
import FormItem from "antd/es/form/FormItem";
import { ErrorMessages, Regex, SidepanelStyles } from "constants/index";
import { useEffect, useMemo, useState } from "react";
import {
  buildUserUnlockMessage,
  copyTextToClipboard
} from "admin-app/utils/user-invite-message";
import { getAccessToken, unlockUserAccountApi } from "services/auth";
import { getCiqClientErrorMessage } from "utils/ciq-client-error";

export type UnlockUserProps = {
  userData: {
    email?: string;
    first_name?: string;
    last_name?: string;
    id?: string;
  };
  setDrawerOpen: (value: {
    showUnlockModel: boolean;
    unlockUserData?: UnlockUserProps["userData"] | null;
  }) => void;
  showDrawerOpen: boolean;
  modelTitle: string;
  onSuccess?: () => void;
};

function UnlockUser(props: UnlockUserProps) {
  const { setDrawerOpen, showDrawerOpen, modelTitle, userData, onSuccess } =
    props;
  const [form] = Form.useForm();
  const [responseLoading, setResponseLoading] = useState(false);
  const [unlockMessage, setUnlockMessage] = useState<string | null>(null);

  const displayName = useMemo(() => {
    const first = userData?.first_name ?? "";
    const last = userData?.last_name ?? "";
    return `${first} ${last}`.trim() || "—";
  }, [userData?.first_name, userData?.last_name]);

  useEffect(() => {
    if (!showDrawerOpen) {
      setUnlockMessage(null);
    }
  }, [showDrawerOpen]);

  const closeDrawer = () => {
    setDrawerOpen({ showUnlockModel: false, unlockUserData: null });
    setUnlockMessage(null);
  };

  const handleCopyUnlockMessage = async () => {
    // unreachable: the Copy button (sole caller) renders only when unlockMessage is truthy
    /* v8 ignore next */
    if (!unlockMessage) return;

    try {
      await copyTextToClipboard(unlockMessage);
      message.success("Unlock message copied to clipboard.");
    } catch {
      message.error("Unable to copy unlock message.");
    }
  };

  const onUnlockUser = async (values: { password: string }) => {
    setResponseLoading(true);
    try {
      const token = getAccessToken();
      if (!token) {
        message.error("Please login to unlock user");
        return;
      }
      if (!userData?.email) {
        message.error("User email is required to unlock account");
        return;
      }

      const response = await unlockUserAccountApi(
        { email: userData.email, password: values.password },
        token
      );

      if (response.data) {
        message.success("User unlocked successfully");
        setUnlockMessage(
          buildUserUnlockMessage({
            firstName: userData?.first_name ?? "",
            email: userData.email,
            password: values.password
          })
        );
        onSuccess?.();
      } else {
        Modal.error({
          title: "Unable to unlock user",
          content: getCiqClientErrorMessage(response, "Failed to unlock user")
        });
      }
    } catch {
      Modal.error({
        title: "Unable to unlock user",
        content: "An error occurred while unlocking user"
      });
    } finally {
      setResponseLoading(false);
    }
  };

  return (
    <Modal
      className="custom-drawer"
      title={modelTitle}
      width={420}
      style={SidepanelStyles.style}
      footer={
        <div className="flex justify-end gap-2">
          {unlockMessage && (
            <Button
              onClick={handleCopyUnlockMessage}
              data-testid="copy-unlock-message-btn"
            >
              Copy Draft Email
            </Button>
          )}
          {unlockMessage ? (
            <Button
              type="primary"
              onClick={closeDrawer}
              data-testid="close-unlock-user-btn"
            >
              Close
            </Button>
          ) : (
            <Button
              form="unlock-user-form"
              htmlType="submit"
              disabled={responseLoading}
              loading={responseLoading}
              type="primary"
              data-testid="unlock-user-submit-btn"
            >
              Unlock User
            </Button>
          )}
        </div>
      }
      open={showDrawerOpen}
      onCancel={closeDrawer}
      destroyOnHidden
    >
      <div className="flex-col h-full overflow-y overflow-x-hidden px-3">
        {unlockMessage && (
          <Alert
            type="success"
            showIcon
            className="mb-3"
            data-testid="unlock-user-success-alert"
            // antd v6 renamed alert internals (ant-alert-message -> ant-alert-title); expose a class-independent testid hook on the message
            title={
              <span data-testid="unlock-user-success-alert-message">
                User unlocked. Copy the unlock message to send to the user.
              </span>
            }
          />
        )}
        <div className="mb-4 space-y-1">
          <div className="text-sm text-gray-500">Name</div>
          <div className="font-medium" data-testid="unlock-user-display-name">
            {displayName}
          </div>
          <div className="text-sm text-gray-500 mt-2">Email</div>
          <div className="font-medium" data-testid="unlock-user-display-email">
            {userData?.email ?? "—"}
          </div>
        </div>
        <Form
          id="unlock-user-form"
          preserve={false}
          onFinish={onUnlockUser}
          form={form}
          scrollToFirstError
          layout="vertical"
          disabled={Boolean(unlockMessage)}
        >
          <FormItem
            name="password"
            label="New Password"
            rules={[
              {
                validateTrigger: "onChange",
                required: true,
                pattern: Regex.password,
                message: ErrorMessages.PasswordRequired
              }
            ]}
            hasFeedback
          >
            <Input.Password placeholder="Enter Password" />
          </FormItem>
          <FormItem
            name="confirm_password"
            label="Confirm Password"
            dependencies={["password"]}
            hasFeedback
            rules={[
              {
                required: true,
                validateTrigger: "onChange",
                message: ErrorMessages.ConfirmPasswordRequired
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(ErrorMessages.ConfirmPasswordRequired)
                  );
                }
              })
            ]}
          >
            <Input.Password placeholder="Confirm Password" />
          </FormItem>
        </Form>
      </div>
    </Modal>
  );
}

export default UnlockUser;
